/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Site-wide scroll-progress bar: a length gate around <ReadingProgressBar>, the
 * same component article pages use. It renders the identical strip (track +
 * emerald fill, pinned to `--header-offset` so it follows the auto-hiding /
 * compacting header) on every page long enough for a progress indicator to
 * mean anything, and nothing at all on short pages where it would read as a
 * stray sliver.
 *
 * Sharing the component rather than re-implementing it is the point: the ask was
 * "behave like the article page", and two copies of a scroll bar drift. The
 * length gate itself lives in hooks/useIsLongPage.ts, shared with the floating
 * compare CTA for the same reason.
 */

import React from 'react';
import { useMatch } from 'react-router-dom';
import { ReadingProgressBar } from './ReadingProgressBar';
import { useIsLongPage } from '../hooks/useIsLongPage';

export const ScrollProgressBar: React.FC = () => {
  // Article pages mount their own <ReadingProgressBar> scoped to the article body
  // rather than the whole document — that one completes at the end of the text,
  // not at the end of the footer, so it is the more meaningful signal. Stand down
  // there instead of stacking two bars on the same 4px strip.
  const onArticle = useMatch('/:lang/kennisbank/:slug') !== null;
  const isLong = useIsLongPage(!onArticle);

  if (onArticle || !isLong) return null;

  // No targetRef → whole-document progress.
  return <ReadingProgressBar />;
};
